import { Icon, Tooltip } from "@smart/itops-sb-design-system-dom";

import { scheduleStillApplies } from "../../shared/submission-actions/reminder-helpers";

export type ReminderColumnInput = {
  status?: string | null;
  sharedAt?: string | null;
  reminder?: {
    automaticCount?: number | null;
    manualCount?: number | null;
    manualStopTriggered?: boolean | null;
    automaticStopReason?: string | null;
  } | null;
};

export type ReminderSchedule =
  | {
      enabled?: boolean | null;
      timezone?: string | null;
      effectiveFrom?: string | null;
      sendFollowUps?: boolean | null;
      maxFollowUps?: number | null;
    }
  | null
  | undefined;

export type ReminderChip = {
  kind: "scheduled" | "manual";
  label: string;
  tooltip: string;
  stopped?: boolean;
  missingEmail?: boolean;
};

const stoppedTooltip = "Scheduled reminders stopped. Follow up manually.";
const missingEmailTooltip =
  "Failed to send. Add an email address to send next cycle.";

const effectiveMax = (schedule: ReminderSchedule) =>
  schedule?.sendFollowUps ? (schedule.maxFollowUps ?? 0) + 1 : 1;

const buildScheduledTooltip = ({
  count,
  denominator,
  onSchedule,
  missingEmail,
  stopped,
}: {
  count: number;
  denominator?: number;
  onSchedule?: boolean;
  missingEmail?: boolean;
  stopped?: boolean;
}) => {
  if (missingEmail) return missingEmailTooltip;
  if (stopped) return stoppedTooltip;

  const countLabel = `${count} scheduled reminder${count === 1 ? "" : "s"} sent`;
  if (!onSchedule || (denominator !== undefined && count > denominator))
    return countLabel;
  if (count === 0) return "Not yet due";
  return `${count} of ${denominator} scheduled reminder${denominator === 1 ? "" : "s"} sent`;
};

const chipIconName = (chip: ReminderChip) => {
  if (chip.missingEmail) return "lightTriangleExclamation";
  if (chip.kind === "scheduled") return "lightCalendarCheck";
  return "lightBell";
};

export const reminderStatusFilterOptions = [
  { value: "scheduled", label: "On a schedule" },
  { value: "manual", label: "Manually" },
  { value: "stopped", label: "Schedule stopped" },
];

export const buildReminderChips = (
  submission: ReminderColumnInput,
  schedule: ReminderSchedule,
): ReminderChip[] => {
  const automaticCount = submission.reminder?.automaticCount ?? 0;
  const manualCount = submission.reminder?.manualCount ?? 0;
  const missingEmail =
    submission.reminder?.automaticStopReason === "missing-contact-email";
  const stopped =
    !!submission.reminder?.manualStopTriggered ||
    !!submission.reminder?.automaticStopReason;
  const onSchedule = scheduleStillApplies(submission, schedule);
  const chips: ReminderChip[] = [];

  const denominator = effectiveMax(schedule);
  const scheduledTooltip = buildScheduledTooltip({
    count: automaticCount,
    denominator,
    onSchedule,
    missingEmail,
    stopped,
  });
  const hasAutoSent = onSchedule || automaticCount > 0;

  if (hasAutoSent) {
    const overMax = automaticCount > denominator;
    chips.push({
      kind: "scheduled",
      label:
        !onSchedule || overMax
          ? `${automaticCount}`
          : `${automaticCount}/${denominator}`,
      tooltip: scheduledTooltip,
      ...(stopped ? { stopped: true } : {}),
      ...(missingEmail ? { missingEmail: true } : {}),
    });
  }

  if (manualCount > 0) {
    const manualTooltip = `${manualCount} manual reminder${manualCount === 1 ? "" : "s"} sent`;
    chips.push({
      kind: "manual",
      label: `${manualCount}`,
      tooltip: manualTooltip,
    });
  }

  return chips;
};

export const getReminderFilterTags = (
  submission: ReminderColumnInput,
  schedule: ReminderSchedule,
) => {
  const chips = buildReminderChips(submission, schedule);
  const tags: string[] = [];

  if (chips.some((chip) => chip.kind === "scheduled" && !chip.stopped)) {
    tags.push("scheduled");
  }
  if (chips.some((chip) => chip.kind === "manual")) {
    tags.push("manual");
  }
  if (chips.some((chip) => chip.stopped)) {
    tags.push("stopped");
  }

  return tags;
};

export const filterByReminderStatus = (
  row: { getValue: (columnId: string) => unknown },
  columnId: string,
  filterValue: unknown,
) => {
  if (
    !filterValue ||
    filterValue === "all" ||
    (Array.isArray(filterValue) && filterValue.length === 0)
  ) {
    return true;
  }

  const tags = row.getValue(columnId);
  if (!Array.isArray(tags)) return false;

  const selected = Array.isArray(filterValue) ? filterValue : [filterValue];
  return selected.some((value) => tags.includes(value));
};

const chipClassName =
  "flex items-center gap-[0.4rem] whitespace-nowrap rounded-large border border-neutral-200 bg-white px-[0.6rem] py-[0.2rem] text-caption text-neutral-800";

export const ReminderCell = ({
  submission,
  setting,
  loading,
}: {
  submission: ReminderColumnInput;
  setting: ReminderSchedule;
  loading: boolean;
}) => {
  if (loading) return null;

  const chips = buildReminderChips(submission, setting);
  if (!chips.length) return <span>-</span>;

  return (
    <div className="flex items-center gap-[0.4rem]">
      {chips.map((chip) => (
        <Tooltip
          key={chip.kind}
          text={chip.tooltip}
          placement="bottom"
          offsetBy={8}
          mountTo="body"
        >
          <span className={chipClassName} data-testid={`reminder-${chip.kind}`}>
            <Icon
              name={chipIconName(chip)}
              className={
                chip.missingEmail
                  ? "h-[1.2rem] w-[1.2rem] text-yellow-450"
                  : "h-[1.2rem] w-[1.2rem]"
              }
            />
            <span className={chip.stopped ? "line-through" : undefined}>
              {chip.label}
            </span>
          </span>
        </Tooltip>
      ))}
    </div>
  );
};
